import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { DialogTitle, DialogContent, DialogActions, Dialog, Button } from '@mui/material';
import { isValid } from 'date-fns';
import { fromZonedTime, format } from 'date-fns-tz';
import DoubleTranslatedCustomRichText from '@/components/CustomRichText/DoubleTranslatedCustomRichText';
import { NewsfeedINT } from '@/containers/Administration/Newsfeed/Newsfeed';
import { DAY_DATE_FORMAT, SHORT_HOUR_DATE_FORMAT } from '@/helpers/constants';
import { stringToDate, formatDate, getFullFormattedDate, changeTimeZoneFormat, convertLocaleToUTC } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import administrationServices from '@/services/administrationServices';
import Calendar from '../UI/Calendar/Calendar';
import InputText from '../UI/InputText/InputText';
import styles from './DialogNewsfeed.module.scss';

export interface NewsfeedCRT extends Omit<NewsfeedINT, 'active' | 'creationDate' | 'creator' | 'publicationDate' | 'expirationDate'> {
  publicationDate: string | Date | null;
  expirationDate: string | Date | null;
  publicationTime: string | Date | null;
  expirationTime: string | Date | null;
}

interface DialogNewsfeedProps {
  close: () => void;
  title: string;
  editNewsfeed?: NewsfeedINT;
  reloadNewsFeed: () => void;
  timeZone: string;
}

const EMPTY_NEWSFEED: NewsfeedCRT = {
  category: '',
  title: { EN: '', DE: '' },
  text: { EN: '', DE: '' },
  publicationDate: stringToDate(''),
  publicationTime: stringToDate(''),
  expirationDate: stringToDate(''),
  expirationTime: stringToDate(''),
  id: '',
};

const mapNewsFeedBackToState = (newsfeed: NewsfeedINT, timeZone: string): NewsfeedCRT => ({
  ...newsfeed,
  publicationDate: getFullFormattedDate(newsfeed.publicationDate, timeZone, DAY_DATE_FORMAT),
  publicationTime: getFullFormattedDate(newsfeed.publicationDate, timeZone, SHORT_HOUR_DATE_FORMAT),
  expirationDate: getFullFormattedDate(newsfeed.expirationDate, timeZone, DAY_DATE_FORMAT),
  expirationTime: getFullFormattedDate(newsfeed.expirationDate, timeZone, SHORT_HOUR_DATE_FORMAT),
});

const DialogNewsfeed: React.FC<DialogNewsfeedProps> = (props) => {
  const { close, title, editNewsfeed = null, reloadNewsFeed, timeZone } = props;

  const isEdition = !!editNewsfeed;
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const [state, setState] = useState<NewsfeedCRT>(isEdition ? mapNewsFeedBackToState(editNewsfeed, timeZone) : EMPTY_NEWSFEED);
  const initialTextRef = useRef(state.text);
  const wysiwygNewsfeedText = 'wysiwyg-newsfeed-text';

  const mapState = () => {
    const timezone = changeTimeZoneFormat(timeZone);
    if ((!(state.publicationDate) || !isValid(state.expirationDate)) || !(state.expirationDate)
      || !isValid(state.expirationDate)) return state;
    const publicationDate = fromZonedTime(`${state.publicationDate} ${state.publicationTime}`, timezone || '');
    const expirationDate = fromZonedTime(`${state.expirationDate} ${state.expirationTime}`, timezone || '');

    return {
      ...state,
      publicationDate: format(convertLocaleToUTC(publicationDate), DAY_DATE_FORMAT),
      publicationTime: format(convertLocaleToUTC(publicationDate), SHORT_HOUR_DATE_FORMAT),
      expirationDate: format(convertLocaleToUTC(expirationDate), DAY_DATE_FORMAT),
      expirationTime: format(convertLocaleToUTC(expirationDate), SHORT_HOUR_DATE_FORMAT),
    };
  };

  const allFieldsFilled = state.title.EN && state.title.DE && state.text.DE && state.text.EN
    && state.publicationDate && state.expirationDate && state.publicationTime && state.expirationTime && state.category;

  const sameDatesAndTimesFromEdit = () => {
    const mapStateData = mapState();

    return (editNewsfeed?.publicationDate.substring(0, 10) === mapStateData?.publicationDate?.toString()
      && editNewsfeed?.publicationDate.substring(11, 16) === mapStateData.publicationTime
      && editNewsfeed?.expirationDate.substring(0, 10) === mapStateData?.expirationDate?.toString()
      && editNewsfeed?.expirationDate.substring(11, 16) === mapStateData.expirationTime);
  };

  const disableButton = () => (isEdition
    // Check if the newsfeed fields has been modified
    ? ((editNewsfeed.title.EN === state.title.EN && editNewsfeed.title.DE === state.title.DE
      && editNewsfeed.text.EN === state.text.EN && editNewsfeed.text.DE === state.text.DE
      && sameDatesAndTimesFromEdit() && editNewsfeed.category === state.category) || !allFieldsFilled)
    // Check if all fields are filled
    : !allFieldsFilled);

  const handleInputChange = (label: string, value: string, language?: string) => {
    let newValue = value;

    if (language) {
      newValue = { ...state[label], [language]: value === '<p><br></p>' ? '' : value };
    }
    setState({ ...state, [label]: newValue });
  };

  const checkAndSaveDate = (value, label) => {
    if (!value) {
      handleInputChange(label, '');
    }

    if (value && value.toString() !== 'Invalid Date') {
      handleInputChange(label, formatDate(value));
    }
  };

  const createNewsfeed = () => {
    administrationServices.createNewsfeed(mapState())
      .then(() => {
        reloadNewsFeed();
        close();
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  };

  const updateNewsfeed = () => {
    administrationServices.editNewsfeed(mapState())
      .then(() => {
        reloadNewsFeed();
        close();
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  };

  return (
    <Dialog open maxWidth="md" fullWidth onClose={close} aria-labelledby="form-dialog-newsfeed">
      <DialogTitle id="form-dialog-newsfeed-title">{title}</DialogTitle>
      <DialogContent>
        <div className={styles.DialogNewsfeed}>
          <div className={styles.RowContainer}>
            <InputText
              id="input-newsfeed-title-en"
              label={`${t('dialog.newsfeedTitle')}_EN`}
              formControlClass={styles.InputSize}
              handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleInputChange('title', ev.target.value, 'EN')}
              value={state.title.EN}
            />
            <InputText
              id="input-newsfeed-title-de"
              label={`${t('dialog.newsfeedTitle')}_DE`}
              formControlClass={styles.InputSize}
              handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleInputChange('title', ev.target.value, 'DE')}
              value={state.title.DE}
            />
          </div>
          <div className={styles.RowContainer} id={wysiwygNewsfeedText}>
            <DoubleTranslatedCustomRichText
              id={wysiwygNewsfeedText}
              title={t('dialog.newsfeedText')}
              initialValue={initialTextRef.current}
              handleInputChange={(language, newValue) => handleInputChange('text', newValue, language)}
            />
          </div>
          <div className={styles.RowContainer}>
            <Calendar
              id="date-picker-publication-date"
              label={t('dialog.newsfeedPublicationDate')}
              formControlClass={styles.Calendar}
              handleChange={(value) => checkAndSaveDate(value, 'publicationDate')}
              value={stringToDate(state.publicationDate)}
            />
            <InputText
              id="input-newsfeed-publication-time"
              label={t('dialog.newsfeedPublicationTime')}
              type="time"
              formControlClass={styles.InputSize}
              handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleInputChange('publicationTime', ev.target.value)}
              value={state.publicationTime?.toString() ?? ''}
            />
          </div>

          <div className={styles.RowContainer}>
            <Calendar
              id="date-picker-expiration-date"
              label={t('dialog.newsfeedExpirationDate')}
              formControlClass={styles.Calendar}
              handleChange={(value) => checkAndSaveDate(value, 'expirationDate')}
              value={stringToDate(state.expirationDate)}
            />
            <InputText
              id="input-newsfeed-expiration-time"
              label={t('dialog.newsfeedExpirationTime')}
              type="time"
              formControlClass={styles.InputSize}
              handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleInputChange('expirationTime', ev.target.value)}
              value={state.expirationTime?.toString() ?? ''}
            />
          </div>
          <div className={styles.RowContainer}>
            <InputText
              id="input-newsfeed-category"
              label={t('dialog.newsfeedCategory')}
              formControlClass={styles.InputSize}
              handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleInputChange('category', ev.target.value)}
              value={state.category}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={disableButton()}
          variant="contained"
          color="primary"
          onClick={isEdition ? updateNewsfeed : createNewsfeed}
        >
          {t(`dialog.${isEdition ? 'edit' : 'save'}`)}
        </Button>
        <Button color="primary" onClick={close}>{t('dialog.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  timeZone: getSettingsInfo(state)?.timezone,
});

export default connect(mapStateToProps)(DialogNewsfeed);
